import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { BookingResponseData, FlightData } from "../@types";
import useInitailizeNewFlight from "../hooks/useInitializeNewFlight";
import useSubmitNewFlightData from "../hooks/useSubmitNewFlightData";
import { APIError } from "../api";

/** Opaque booking id from the last submit. Not used to authorise payment. */
export const TRIP_BOOKING_ID_KEY = "trip_booking_id";

export type TripsContextV = {
  booking: boolean;
  initializingNewFlight: boolean;
  isInitializeError: boolean;
  retryInitialization: () => void;
  bookingResponse: BookingResponseData | null;
  bookingURL: string | null;
  submitingNewFlightData: boolean;
  submitNewFlightDataError: APIError | null;
  isSubmitingNewFlightDataError: boolean;
  flightData: FlightData | null;
  setBooking: React.Dispatch<React.SetStateAction<boolean>>;
  setBookingResponse: React.Dispatch<
    React.SetStateAction<BookingResponseData | null>
  >;
  /**
   * Put back a booking the server has just confirmed belongs to this customer
   * and is unpaid, after a setup redirect reloaded the app. See
   * utils/checkoutResume.
   */
  restoreBooking: (booking: FlightData) => void;
};

export const TripsContext = React.createContext<TripsContextV>({
  booking: false,
  bookingResponse: null,
  bookingURL: "",
  initializingNewFlight: false,
  isInitializeError: false,
  retryInitialization() {},
  flightData: null,
  submitingNewFlightData: true,
  submitNewFlightDataError: null,
  isSubmitingNewFlightDataError: false,
  setBooking() {
    // do nothing
  },
  setBookingResponse() {
    // do nothing
  },
  restoreBooking() {
    // do nothing
  },
});

export const useTrips = () => {
  const context = useContext(TripsContext);

  return context;
};

const TripsContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [booking, setBooking] = useState(false);
  const [restoredFlight, setRestoredFlight] = useState<FlightData | null>(null);

  const [bookingURL, setBookingURL] = useState<string | null>(null);

  const {
    submitNewFlightData,
    flightData,
    reset,
    submitingNewFlightData,
    submitNewFlightDataError,
    isSubmitingNewFlightDataError,
  } = useSubmitNewFlightData({
    onSuccess(data) {
      if (data.data?.id) {
        localStorage.setItem(TRIP_BOOKING_ID_KEY, data?.data?.id);
      }
    },
    onError(error) {},
    onMutate(variables) {},
  });

  const [bookingResponse, setBookingResponse] =
    useState<BookingResponseData | null>(null);

  const { initializingNewFlight, isInitializeError, reinitializeFlight } = useInitailizeNewFlight({
    onSuccess(data) {
      reset();
      if (data.data) {
        setBookingURL(data?.data.url);
      }
    },
  });

  useEffect(() => {
    if (bookingResponse) {
      submitNewFlightData({
        widget_data: bookingResponse,
        origin: "web",
      });
    }
  }, [bookingResponse, submitNewFlightData]);

  useEffect(() => {
    if (booking) {
      // Drop the previous search's payload, submit result and id so a failed
      // initialise cannot leave a stale booking behind or resubmit an old
      // payload. The widget only returns a payload after `booking` goes false,
      // so a freshly submitted booking is never cleared here.
      setBookingResponse(null);
      setRestoredFlight(null);
      reset();
      localStorage.removeItem(TRIP_BOOKING_ID_KEY);
      reinitializeFlight();
    }

    return () => {};
  }, [booking, reinitializeFlight, reset]);

  return (
    <TripsContext.Provider
      value={{
        booking,
        setBooking,
        bookingResponse,
        bookingURL,
        setBookingResponse,
        initializingNewFlight,
        isInitializeError,
        retryInitialization: () => { void reinitializeFlight(); },
        flightData: flightData?.data || restoredFlight,
        restoreBooking: setRestoredFlight,
        submitingNewFlightData,
        submitNewFlightDataError,
        isSubmitingNewFlightDataError,
      }}
    >
      {children}
    </TripsContext.Provider>
  );
};

export default TripsContextProvider;
